import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import PanelTemplate from "../../templates/Panel";
import Input from "../../components/Input";
import Button from "../../components/Button";
import { Loading } from "../../components/Loading";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { hasPlanFeature } from "../../services/auth";
import { PlanFeatureUpgradeModal } from "../../components/PlanFeatureUpgradeModal";
import { PlanFeatureUpgradeButton } from "../../components/PlanFeatureUpgradeButton";
import PanelIntro from "../Integration/tabs/PanelIntro";
import { WebhookArt } from "../Integration/tabs/IntroArt";

import "../Integration/tabs/panels.scss";

const WebhookIntroIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const EVENTOS_PEDIDOS = [
    { value: "created", label: "Criado" },
    { value: "updated", label: "Atualizado" },
    { value: "deleted", label: "Excluído" },
];

const EVENTOS_ORCAMENTOS = [
    { value: "created", label: "Criado" },
    { value: "updated", label: "Atualizado" },
    { value: "deleted", label: "Excluído" },
];

const EVENTOS_PROPOSTA = [
    { value: "created", label: "Criada" },
];

const EVENTOS_EMPRESAS = [
    { value: "created", label: "Criada" },
    { value: "updated", label: "Atualizada" },
    { value: "deleted", label: "Excluída" },
];

const EVENTOS_PESSOAS = [
    { value: "created", label: "Criada" },
    { value: "updated", label: "Atualizada" },
    { value: "deleted", label: "Excluída" },
];

const DEFAULT_STRUCTURE = {
    order: {
        created: { url: "", ativo: false },
        updated: { url: "", ativo: false },
        deleted: { url: "", ativo: false },
    },
    budget: {
        created: { url: "", ativo: false },
        updated: { url: "", ativo: false },
        deleted: { url: "", ativo: false },
    },
    proposal: {
        created: { url: "", ativo: false },
    },
    company: {
        created: { url: "", ativo: false },
        updated: { url: "", ativo: false },
        deleted: { url: "", ativo: false },
    },
    customer: {
        created: { url: "", ativo: false },
        updated: { url: "", ativo: false },
        deleted: { url: "", ativo: false },
    },
};

const ENDPOINT_ERROR_MESSAGE = "Verifique o endpoint";

const createEmptyErrors = () => ({
    order: Object.keys(DEFAULT_STRUCTURE.order).reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    budget: Object.keys(DEFAULT_STRUCTURE.budget).reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    proposal: Object.keys(DEFAULT_STRUCTURE.proposal).reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    company: Object.keys(DEFAULT_STRUCTURE.company).reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    customer: Object.keys(DEFAULT_STRUCTURE.customer).reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
});

const mapStateToApiPayload = (eventosState = {}) =>
    Object.keys(eventosState).reduce((acc, key) => {
        const item = eventosState[key] ?? {};
        acc[key] = {
            url: item.url ?? "",
            is_active: Boolean(item.ativo),
        };
        return acc;
    }, {});

const hasValidationError = (eventData = {}) => {
    const status = String(
        eventData.status ?? eventData.validation_status ?? eventData.result ?? ""
    ).toLowerCase();

    if (["error", "failed", "invalid", "unreachable", "fail"].includes(status)) {
        return true;
    }

    if (
        eventData.valid === false ||
        eventData.success === false ||
        eventData.ok === false ||
        eventData.is_valid === false
    ) {
        return true;
    }

    return false;
};

const isInactiveEndpoint = (eventData = {}) => {
    const status = String(eventData.status ?? "").toLowerCase();
    if (status === "inactive") {
        return true;
    }

    if (eventData.is_active === false) {
        return true;
    }

    if (eventData.ativo === false) {
        return true;
    }

    return false;
};

const extractValidationErrorsFromResource = (resource = {}) => {
    const nextErrors = createEmptyErrors();
    let hasAnyError = false;

    Object.keys(DEFAULT_STRUCTURE).forEach((group) => {
        Object.keys(DEFAULT_STRUCTURE[group]).forEach((eventName) => {
            const eventData = resource?.[group]?.[eventName];
            if (isInactiveEndpoint(eventData)) {
                return;
            }
            if (hasValidationError(eventData)) {
                nextErrors[group][eventName] = ENDPOINT_ERROR_MESSAGE;
                hasAnyError = true;
            }
        });
    });

    (resource.invalid_endpoints ?? []).forEach((path) => {
        if (typeof path !== "string") return;
        const [group, eventName] = path.split(".");
        const eventData = resource?.[group]?.[eventName];
        if (
            nextErrors[group]?.[eventName] !== undefined &&
            !isInactiveEndpoint(eventData)
        ) {
            nextErrors[group][eventName] = ENDPOINT_ERROR_MESSAGE;
            hasAnyError = true;
        }
    });

    return { errors: nextErrors, hasAnyError };
};

const extractValidationErrorsFromFields = (fields = {}, payloadState = {}) => {
    const nextErrors = createEmptyErrors();
    let hasAnyError = false;

    Object.keys(fields).forEach((fieldPath) => {
        Object.keys(DEFAULT_STRUCTURE).forEach((group) => {
            Object.keys(DEFAULT_STRUCTURE[group]).forEach((eventName) => {
                const dotPath = `${group}.${eventName}`;
                const bracketPath = `${group}[${eventName}]`;
                if (fieldPath.includes(dotPath) || fieldPath.includes(bracketPath)) {
                    const submittedEvent = payloadState?.[group]?.[eventName];
                    if (submittedEvent?.ativo === false) {
                        return;
                    }
                    nextErrors[group][eventName] = ENDPOINT_ERROR_MESSAGE;
                    hasAnyError = true;
                }
            });
        });
    });

    return { errors: nextErrors, hasAnyError };
};

const mergeEventosState = (defaultState, apiState, prevState) => {
    if (!apiState) return defaultState;
    const result = {};
    for (const key of Object.keys(defaultState)) {
        const apiEvent = apiState[key] ?? { url: "", ativo: false };
        const prevEvent = prevState?.[key] ?? { url: "", ativo: false };
        const normalizedStatus = String(apiEvent.status ?? "").toLowerCase();
        const ativo =
            apiEvent.is_active ??
            apiEvent.ativo ??
            (normalizedStatus === "active"
                ? true
                : normalizedStatus === "inactive"
                    ? false
                    : prevEvent.ativo);
        result[key] = {
            url: apiEvent.url || (ativo === false ? prevEvent.url : ""),
            ativo: Boolean(ativo),
        };
    }
    return result;
};

const iconProps = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

const GROUP_META = {
    order: {
        bg: "#EEEDFE",
        fg: "#362B7A",
        icon: (
            <svg {...iconProps}>
                <circle cx="9" cy="20" r="1.3" />
                <circle cx="18" cy="20" r="1.3" />
                <path d="M2 3h2.2l2.2 11.2a1.5 1.5 0 0 0 1.5 1.2h8.4a1.5 1.5 0 0 0 1.5-1.2L19.6 6H5" />
            </svg>
        ),
    },
    budget: {
        bg: "#E1F5EE",
        fg: "#0F6E56",
        icon: (
            <svg {...iconProps}>
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
                <path d="M14 3v5h5" />
                <path d="M9 13h6M9 17h6" />
            </svg>
        ),
    },
    company: {
        bg: "#E6F1FB",
        fg: "#185FA5",
        icon: (
            <svg {...iconProps}>
                <rect x="4" y="3" width="16" height="18" rx="1.5" />
                <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
            </svg>
        ),
    },
    proposal: {
        bg: "#FAEEDA",
        fg: "#854F0B",
        icon: (
            <svg {...iconProps}>
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
                <path d="M14 3v5h5" />
                <path d="m9 15 2 2 4-4" />
            </svg>
        ),
    },
    customer: {
        bg: "#FBEAF0",
        fg: "#993556",
        icon: (
            <svg {...iconProps}>
                <circle cx="9" cy="8" r="3" />
                <path d="M3 20a6 6 0 0 1 12 0" />
                <path d="M16 6.5a3 3 0 0 1 0 5.8" />
                <path d="M21 20a6 6 0 0 0-4-5.6" />
            </svg>
        ),
    },
};

const Toggle = ({ active, onChange }) => (
    <label className="switch" title={active ? "Ativo" : "Inativo"}>
        <input
            type="checkbox"
            checked={active}
            onChange={() => onChange(!active)}
        />
        <span className="switch__track" />
    </label>
);

const WebhookCard = ({
    title,
    group,
    eventos,
    eventosState,
    setEventosState,
    eventosErrorsState,
    setEventosErrorsState,
}) => {
    const meta = GROUP_META[group] ?? {};

    const setEvento = (value, field, newValue) => {
        setEventosState((prev) => ({
            ...prev,
            [value]: { ...prev[value], [field]: newValue },
        }));
        if (setEventosErrorsState) {
            setEventosErrorsState((prev) => ({
                ...prev,
                [value]: "",
            }));
        }
    };

    return (
        <div className="panel-card">
            <div className="wh-card__title">
                <span className="wh-chip" style={{ background: meta.bg, color: meta.fg }}>
                    {meta.icon}
                </span>
                {title}
            </div>
            <div className="wh-events">
                {eventos.map((ev) => {
                    const { url, ativo } = eventosState[ev.value] || { url: "", ativo: false };
                    return (
                        <div key={ev.value} className={`wh-event${ativo ? "" : " is-off"}`}>
                            <div className="wh-event__top">
                                <span className="wh-event__name">{ev.label}</span>
                                <Toggle
                                    active={ativo}
                                    onChange={(v) => setEvento(ev.value, "ativo", v)}
                                />
                            </div>
                            <Input
                                placeholder="https://seu-endpoint.com/webhook"
                                value={url}
                                change={(v) => setEvento(ev.value, "url", v)}
                                disabled={!ativo}
                                error={eventosErrorsState?.[ev.value]}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Webhook = ({ embedded = false }) => {
    const [showUpgrade, setShowUpgrade] = useState(false);
    const planAvailable = hasPlanFeature("webhook");
    const [loadingInitial, setLoadingInitial] = useState(true);
    const [loadingSave, setLoadingSave] = useState(false);
    const [chave, setChave] = useState("");
    const [orderEventosState, setOrderEventosState] = useState(DEFAULT_STRUCTURE.order);
    const [budgetEventosState, setBudgetEventosState] = useState(DEFAULT_STRUCTURE.budget);
    const [proposalEventosState, setProposalEventosState] = useState(DEFAULT_STRUCTURE.proposal);
    const [companyEventosState, setCompanyEventosState] = useState(DEFAULT_STRUCTURE.company);
    const [customerEventosState, setCustomerEventosState] = useState(DEFAULT_STRUCTURE.customer);
    const [endpointErrors, setEndpointErrors] = useState(createEmptyErrors());

    const fetchWebhookConfig = () => {
        setLoadingInitial(true);
        api.get("/webhook")
            .then((res) => {
                const resource = res.data?.resource ?? {};
                setChave(resource.chave ?? "");
                setOrderEventosState((prev) =>
                    mergeEventosState(DEFAULT_STRUCTURE.order, resource.order, prev)
                );
                setBudgetEventosState((prev) =>
                    mergeEventosState(DEFAULT_STRUCTURE.budget, resource.budget, prev)
                );
                setProposalEventosState((prev) =>
                    mergeEventosState(DEFAULT_STRUCTURE.proposal, resource.proposal, prev)
                );
                setCompanyEventosState((prev) =>
                    mergeEventosState(DEFAULT_STRUCTURE.company, resource.company, prev)
                );
                setCustomerEventosState((prev) =>
                    mergeEventosState(DEFAULT_STRUCTURE.customer, resource.customer, prev)
                );
            })
            .catch(renderError)
            .finally(() => setLoadingInitial(false));
    };

    useEffect(() => {
        if (!planAvailable) return;
        fetchWebhookConfig();
    }, []);

    const handleSave = () => {
        setLoadingSave(true);
        setEndpointErrors(createEmptyErrors());
        api.put("/webhook", {
            chave,
            order: mapStateToApiPayload(orderEventosState),
            budget: mapStateToApiPayload(budgetEventosState),
            proposal: mapStateToApiPayload(proposalEventosState),
            company: mapStateToApiPayload(companyEventosState),
            customer: mapStateToApiPayload(customerEventosState),
        })
            .then((res) => {
                const resource = res.data?.resource;
                const validation = extractValidationErrorsFromResource(resource);
                setEndpointErrors(validation.errors);

                if (validation.hasAnyError) {
                    toast.error("Alguns endpoints falharam na validação");
                } else {
                    toast.success("Configuração salva com sucesso");
                }

                if (resource) {
                    setChave(resource.chave ?? "");
                    setOrderEventosState((prev) =>
                        mergeEventosState(DEFAULT_STRUCTURE.order, resource.order, prev)
                    );
                    setBudgetEventosState((prev) =>
                        mergeEventosState(DEFAULT_STRUCTURE.budget, resource.budget, prev)
                    );
                    setProposalEventosState((prev) =>
                        mergeEventosState(DEFAULT_STRUCTURE.proposal, resource.proposal, prev)
                    );
                    setCompanyEventosState((prev) =>
                        mergeEventosState(DEFAULT_STRUCTURE.company, resource.company, prev)
                    );
                    setCustomerEventosState((prev) =>
                        mergeEventosState(DEFAULT_STRUCTURE.customer, resource.customer, prev)
                    );
                }
            })
            .catch((error) => {
                const validation = extractValidationErrorsFromFields(error?.response?.data?.fields, {
                    order: orderEventosState,
                    budget: budgetEventosState,
                    proposal: proposalEventosState,
                    company: companyEventosState,
                    customer: customerEventosState,
                });
                if (validation.hasAnyError) {
                    setEndpointErrors(validation.errors);
                    toast.error("Alguns endpoints falharam na validação");
                    return;
                }
                renderError(error);
            })
            .finally(() => setLoadingSave(false));
    };

    const buildErrorSetter = (group) => (setter) =>
        setEndpointErrors((prev) => ({
            ...prev,
            [group]: typeof setter === "function" ? setter(prev[group]) : setter,
        }));

    const wrap = (children) =>
        embedded ? children : <PanelTemplate id="webhook">{children}</PanelTemplate>;

    if (!planAvailable) {
        return wrap(
            <div className="wh-panel">
                <PanelIntro
                    title="O que é um Webhook?"
                    icon={WebhookIntroIcon}
                    iconBg="#FAEEDA"
                    iconFg="#854F0B"
                    art={<WebhookArt />}
                >
                    <p>
                        Um Webhook é uma forma da Brinde.me avisar o seu sistema
                        automaticamente sempre que algo acontece — um pedido criado,
                        um orçamento atualizado, uma empresa cadastrada. Em vez de
                        ficar consultando a API periodicamente, o seu sistema recebe
                        a notificação na hora.
                    </p>
                </PanelIntro>
                <div className="panel-card">
                    <h2>Configuração de Webhooks</h2>
                    <p className="panel-card__sub">
                        Este recurso não está disponível no seu plano atual.
                        Faça upgrade para receber notificações em tempo real no seu sistema.
                    </p>
                    <PlanFeatureUpgradeButton onClick={() => setShowUpgrade(true)} text="Fazer upgrade de plano" />
                </div>
                <PlanFeatureUpgradeModal
                    feature="webhook"
                    show={showUpgrade}
                    close={() => setShowUpgrade(false)}
                />
            </div>
        );
    }

    if (loadingInitial) {
        return wrap(<Loading show={true} />);
    }

    return wrap(
        <div className="wh-panel">
            <PanelIntro
                title="O que é um Webhook?"
                icon={WebhookIntroIcon}
                iconBg="#FAEEDA"
                iconFg="#854F0B"
                art={<WebhookArt />}
            >
                <p>
                    Se a API é você perguntando algo à Brinde.me, o webhook é a{" "}
                    Brinde.me te avisando. Você cadastra um endereço (URL) e, sempre
                    que um evento acontecer (um orçamento criado, um pedido aprovado) a Brinde.me
                    envia os dados completos para o seu sistema, em tempo real.
                </p>
                <div className="panel-intro__example">
                    <strong>Exemplo:</strong> ao aprovar um pedido na Brinde.me, o webhook avisa seu
                    ERP na hora e ele lança o pedido sozinho — sem ninguém digitar nada.
                </div>
            </PanelIntro>

            <div className="panel-card wh-secret">
                <div className="wh-secret__head">
                    <h2>Chave secreta</h2>
                    <span className="badge-optional">Opcional</span>
                </div>
                <p className="panel-card__sub">
                    Defina um valor secreto que a Brinde.me enviará no header{" "}
                    <code className="inline-code">X-Webhook-Secret</code> em todas as requisições.
                    Use-o no seu sistema para confirmar que a chamada veio mesmo da Brinde.me.
                </p>
                <Input
                    placeholder="Ex: sua-chave-secreta"
                    value={chave}
                    change={setChave}
                />
            </div>

            <div className="wh-groups">
                <WebhookCard
                    title="Pedidos"
                    group="order"
                    eventos={EVENTOS_PEDIDOS}
                    eventosState={orderEventosState}
                    setEventosState={setOrderEventosState}
                    eventosErrorsState={endpointErrors.order}
                    setEventosErrorsState={buildErrorSetter("order")}
                />
                <WebhookCard
                    title="Orçamentos"
                    group="budget"
                    eventos={EVENTOS_ORCAMENTOS}
                    eventosState={budgetEventosState}
                    setEventosState={setBudgetEventosState}
                    eventosErrorsState={endpointErrors.budget}
                    setEventosErrorsState={buildErrorSetter("budget")}
                />
                <WebhookCard
                    title="Empresas"
                    group="company"
                    eventos={EVENTOS_EMPRESAS}
                    eventosState={companyEventosState}
                    setEventosState={setCompanyEventosState}
                    eventosErrorsState={endpointErrors.company}
                    setEventosErrorsState={buildErrorSetter("company")}
                />
                <WebhookCard
                    title="Proposta"
                    group="proposal"
                    eventos={EVENTOS_PROPOSTA}
                    eventosState={proposalEventosState}
                    setEventosState={setProposalEventosState}
                    eventosErrorsState={endpointErrors.proposal}
                    setEventosErrorsState={buildErrorSetter("proposal")}
                />
                <WebhookCard
                    title="Pessoas"
                    group="customer"
                    eventos={EVENTOS_PESSOAS}
                    eventosState={customerEventosState}
                    setEventosState={setCustomerEventosState}
                    eventosErrorsState={endpointErrors.customer}
                    setEventosErrorsState={buildErrorSetter("customer")}
                />
            </div>

            <div className="wh-save">
                <Button
                    type="primary"
                    size="small"
                    svg="save-white"
                    text="Salvar alterações"
                    action={handleSave}
                    loading={loadingSave}
                />
            </div>
        </div>
    );
};

export default Webhook;
